/**
 * Generic keyed LRU cache with optional capacity and eviction callback.
 */
export class LruCache<K, V> {
  private map = new Map<K, V>();

  constructor(
    private readonly capacity: number,
    private readonly onEvict?: (value: V, key: K) => void,
  ) {}

  get(key: K): V | undefined {
    const value = this.map.get(key);
    if (value === undefined) return undefined;
    // Re-insert to mark as most recently used.
    this.map.delete(key);
    this.map.set(key, value);
    return value;
  }

  set(key: K, value: V): void {
    if (this.map.has(key)) this.map.delete(key);
    this.map.set(key, value);
    while (this.map.size > this.capacity) {
      const oldest = this.map.keys().next();
      if (oldest.done) break;
      const evicted = this.map.get(oldest.value)!;
      this.map.delete(oldest.value);
      this.onEvict?.(evicted, oldest.value);
    }
  }

  has(key: K): boolean {
    return this.map.has(key);
  }

  clear(): void {
    for (const [k, v] of this.map) this.onEvict?.(v, k);
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }
}

/**
 * A doubly-linked-list based Least Recently Used (LRU) cache for point cloud nodes.
 * Tracks loaded nodes and manages memory by disposing least-recently-used nodes
 * when total points exceed the configured limit.
 */

export interface LRUNode {
  id: string | number;
  loaded: boolean;
  numPoints: number;
  children: Record<string, LRUNode>;
  dispose(): void;
}

class LRUItem<T extends LRUNode = LRUNode> {
  previous: LRUItem<T> | null = null;
  next: LRUItem<T> | null = null;
  constructor(public readonly node: T) {}
}

export class LRU<T extends LRUNode = LRUNode> {
  private first: LRUItem<T> | null = null;
  private last: LRUItem<T> | null = null;
  private items: Record<string | number, LRUItem<T>> = {};
  private elements = 0;
  numPoints = 0;

  size(): number {
    return this.elements;
  }

  contains(node: T): boolean {
    return this.items[node.id] != null;
  }

  touch(node: T): void {
    if (!node.loaded) {
      return;
    }

    let item: LRUItem<T>;
    if (this.items[node.id] == null) {
      // add to list
      item = new LRUItem(node);
      item.previous = this.last;
      this.last = item;
      if (item.previous !== null) {
        item.previous.next = item;
      }

      this.items[node.id] = item;
      this.elements++;

      if (this.first === null) {
        this.first = item;
      }
      this.numPoints += node.numPoints;
    } else {
      // update in list
      item = this.items[node.id];
      if (item.previous === null) {
        // handle touch on first element
        if (item.next !== null) {
          this.first = item.next;
          this.first.previous = null;
          item.previous = this.last;
          item.next = null;
          this.last = item;
          item.previous!.next = item;
        }
      } else if (item.next === null) {
        // handle touch on last element — already most recent
      } else {
        // handle touch on any other element
        item.previous.next = item.next;
        item.next.previous = item.previous;
        item.previous = this.last;
        item.next = null;
        this.last = item;
        item.previous!.next = item;
      }
    }
  }

  remove(node: T): void {
    const lruItem = this.items[node.id];
    if (lruItem) {
      if (this.elements === 1) {
        this.first = null;
        this.last = null;
      } else {
        if (!lruItem.previous) {
          this.first = lruItem.next;
          this.first!.previous = null;
        }
        if (!lruItem.next) {
          this.last = lruItem.previous;
          this.last!.next = null;
        }
        if (lruItem.previous && lruItem.next) {
          lruItem.previous.next = lruItem.next;
          lruItem.next.previous = lruItem.previous;
        }
      }

      delete this.items[node.id];
      this.elements--;
      this.numPoints -= node.numPoints;
    }
  }

  getLRUItem(): T | null {
    if (this.first === null) {
      return null;
    }
    return this.first.node;
  }

  freeMemory(pointLoadLimit: number): void {
    if (this.elements <= 1) {
      return;
    }

    let iterations = 0;
    const maxIterations = this.elements;
    while (this.numPoints > pointLoadLimit && iterations < maxIterations) {
      const element = this.first;
      if (!element) break;
      const node = element.node;
      this.disposeDescendants(node);
      iterations++;
    }
  }

  disposeDescendants(node: T): void {
    const stack: T[] = [];
    stack.push(node);
    while (stack.length > 0) {
      const current = stack.pop()!;
      current.dispose();
      this.remove(current);

      for (const key in current.children) {
        if (current.children.hasOwnProperty(key)) {
          const child = current.children[key] as T;
          if (child.loaded) {
            stack.push(child);
          }
        }
      }
    }
  }

  toString(): string {
    let string = '{ ';
    let curr = this.first;
    while (curr !== null) {
      string += curr.node.id;
      if (curr.next !== null) {
        string += ', ';
      }
      curr = curr.next;
    }
    string += '}';
    string += '(' + this.size() + ')';
    return string;
  }
}
